import { LocalDateTime } from "@js-joda/core";
import { queryOptions } from "@tanstack/react-query";
import { ResponseOf } from "../../core/api";
import { createQueryGroup } from "../../core/api/queries";
import {
  CaregiverId,
  PlanOfCareDutyId,
  PlanOfCareItemId,
  VisitInstanceId,
} from "../../shared/schema/schema";

/**
 * A make-up this visit has already handed to another visit: which duty, how many
 * occurrences of it, and the visit now carrying them. The server's
 * `VisitInstanceDutyMakeUp`.
 *
 * Spelled out here rather than taken from `Messages` because `existingMakeUps`
 * reached the response after the last `pnpm openapi:local`, so neither the type
 * nor the response validator knows the field yet. Delete this and read the
 * generated type the next time the schema is regenerated.
 */
export type VisitInstanceDutyMakeUp = {
  planOfCareDutyId: PlanOfCareDutyId;
  planOfCareItemId: PlanOfCareItemId;
  /** How many occurrences of the duty this make-up moved. At least one. */
  occurrences: number;
  targetVisitInstanceId: VisitInstanceId;
  targetStartTime: LocalDateTime;
  targetEndTime: LocalDateTime;
  targetCaregiverId: CaregiverId | null;
  targetCaregiverName: string | null;
};

type GeneratedMissedDutiesResponse = ResponseOf<
  "get",
  "./visit_instances/:visitInstanceId/missed_duties"
>;

/**
 * What the endpoint actually answers. `existingMakeUps` is optional and its
 * timestamps may be strings for the same one reason: the generated schema does
 * not know the field, so nothing validated or revived it.
 */
type RawMissedDutiesResponse = GeneratedMissedDutiesResponse & {
  existingMakeUps?: (Omit<VisitInstanceDutyMakeUp, "targetStartTime" | "targetEndTime"> & {
    targetStartTime: LocalDateTime | string;
    targetEndTime: LocalDateTime | string;
  })[];
};

/** The same response once this module has revived what the validator skipped. */
export type MissedDutiesResponse = GeneratedMissedDutiesResponse & {
  existingMakeUps: VisitInstanceDutyMakeUp[];
};

export type MissedDuty = MissedDutiesResponse["missedDuties"][number];

/**
 * A `LocalDateTime` the response validator did not reach.
 *
 * Validation and js-joda revival both run off the generated schema, and
 * `existingMakeUps` is not in it: the object is passed through untouched, which
 * means its two timestamps arrive as the raw ISO strings the server sent while
 * every other date on the response is already a `LocalDateTime`. Accepting both
 * is what lets this keep working — rather than start throwing — the moment the
 * schema is regenerated and the reviver does reach them.
 */
function reviveLocalDateTime(value: LocalDateTime | string): LocalDateTime {
  return typeof value === "string" ? LocalDateTime.parse(value) : value;
}

export default createQueryGroup(({ api }) => ({
  missedDuties: (visitInstanceId: VisitInstanceId) =>
    queryOptions({
      queryKey: ["visitInstance", visitInstanceId, "missedDuties"],
      queryFn: async (): Promise<MissedDutiesResponse> => {
        const response: RawMissedDutiesResponse = await api.get(
          "./visit_instances/:visitInstanceId/missed_duties",
          { path: { visitInstanceId } }
        );

        return {
          ...response,
          existingMakeUps: (response.existingMakeUps ?? []).map((makeUp) => ({
            ...makeUp,
            targetStartTime: reviveLocalDateTime(makeUp.targetStartTime),
            targetEndTime: reviveLocalDateTime(makeUp.targetEndTime),
          })),
        };
      },
    }),
}));
